import { Text, Flex, Icon, Image, LinkButton, ButtonGroup } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import HombotIcon from '../../../../assets/logos/homebot-icon.svg'
import { routeToHome } from '../../../../store/selectors/routes'
import { useSelector } from 'react-redux'
import { selectClientId } from '../../../../store/selectors/client'
import { hasCustomerToken } from '../../../../auth'
import { Link as ReactRouterLink } from 'react-router-dom'
import { useTheme } from '@homebotapp/hb-react-component-catalog'
import { ListingAddress } from '../../../../api/gqlaxy/gql/generated/graphql'
import { formatListingAddress } from '../../../../helpers/formatListingAddress'
import { DismissButton } from '../../../../features/ForYouContent/components/DismissButton'
import useViewport from '../../../../hooks/useViewport'

export const MSG = defineMessages({
  header: {
    id: 'HomeSearch.listingDetails.ownThisHome.header',
    defaultMessage: 'Do you own this home?'
  },
  description: {
    id: 'HomeSearch.listingDetails.ownThisHome.description',
    defaultMessage: 'Add your address to see its worth and get personalized financial insights'
  },
  claim: {
    id: 'HomeSearch.listingDetails.ownThisHome.claim',
    defaultMessage: 'Claim this home'
  },
  enterAddress: {
    id: 'HomeSearch.listingDetails.ownThisHome.enterAddress',
    defaultMessage: 'Enter a different address'
  },
  alreadyOwnAHome: {
    id: 'HomeSearch.listingDetails.ownThisHome.alreadyOwnAHome',
    defaultMessage: 'Already own a home?'
  },
  letsGo: {
    id: 'HomeSearch.listingDetails.ownThisHome.letsGo',
    defaultMessage: "Let's go"
  },
  getStarted: {
    id: 'HomeSearch.listingDetails.ownThisHome.getStarted',
    defaultMessage: 'Get started'
  }
})

interface OwnAHomeProps {
  address?: ListingAddress
  enableDismiss?: boolean
  onDismiss?: () => void
}

export const OwnAHome = ({ address, enableDismiss = false, onDismiss }: OwnAHomeProps) => {
  const intl = useIntl()
  const { theme } = useTheme()
  const clientId = useSelector(selectClientId)
  const impersonatedClientId = localStorage.getItem('impersonatedClientId')
  const viewport = useViewport()

  const handleClaim = (): string => {
    return buildRouteToHome() + '?claim-address=' + encodeURIComponent(formatListingAddress(address))
  }

  const handleEnterAddress = (): string => {
    return buildRouteToHome()
  }

  const buildRouteToHome = (): string => {
    return hasCustomerToken() && !clientId ? routeToHome(impersonatedClientId, 'new') : routeToHome(clientId, 'new')
  }

  return (
    <Flex
      borderRadius='xl'
      border='1px solid'
      borderColor={theme.isLightThemeType ? 'neutral.300' : 'whiteAlpha.300'}
      maxWidth={enableDismiss ? '800px' : '100%'}
      mx='auto'
      my={6}
      px={[6, 6, 10]}
      py={[4, 4, 6]}
      flexDirection='column'
      backgroundImage={
        theme.isLightThemeType
          ? 'linear-gradient(87.21deg, #FDDEF6 16.21%, #F9E8FD 96.43%)'
          : 'linear-gradient(to right, #220051, #31006F)'
      }
    >
      <Flex flexDirection={viewport.isMedium ? 'column' : 'row'} justifyContent='space-between' alignItems='center'>
        <Flex flexDirection='row' alignItems='center'>
          <Image src={HombotIcon} aria-hidden='true' alt='' height={12} pr={4} />
          <Flex flexDirection='column'>
            <Text size='lg' color='neutral.900' fontWeight={700} mb={0}>
              {address ? intl.formatMessage(MSG.header) : intl.formatMessage(MSG.alreadyOwnAHome)}
            </Text>
            <Text size='xs' color='neutral.700' fontWeight={400}>
              {intl.formatMessage(MSG.description)}
            </Text>
          </Flex>
        </Flex>
        {enableDismiss && (
          <ButtonGroup width={viewport.isMedium ? '100%' : 'auto'}>
            <LinkButton
              as={ReactRouterLink}
              rightIcon={<Icon name='circle-arrow-right-bts' />}
              size='sm'
              backgroundColor={theme.isLightThemeType ? 'brand.500' : 'brand.400'}
              color='white'
              _hover={{
                backgroundColor: theme.isLightThemeType ? 'brand.600' : 'brand.500',
                color: 'white'
              }}
              w='100%'
              to={buildRouteToHome}
              tracking={{
                guid: '56wYViZ3SkqASKbcAhsMVo',
                ui_context: 'Own a Home',
                descriptor: 'get started button'
              }}
            >
              {intl.formatMessage(MSG.getStarted)}
            </LinkButton>
            <DismissButton trackingContext='Own a Home' onClick={onDismiss} />
          </ButtonGroup>
        )}
      </Flex>
      {!enableDismiss && (
        <Flex flexDirection={['column', 'column', 'row']} mt={4} gap={2}>
          {address && (
            <>
              <LinkButton
                as={ReactRouterLink}
                rightIcon={<Icon name='circle-arrow-right-bts' />}
                size='md'
                backgroundColor={theme.isLightThemeType ? 'brand.500' : 'brand.400'}
                color='white'
                _hover={{
                  backgroundColor: theme.isLightThemeType ? 'brand.600' : 'brand.500',
                  color: 'white'
                }}
                tracking={{
                  guid: 'kiaM8J92StEZU8U9mtBkNJ',
                  ui_context: 'Own a Home',
                  descriptor: 'Claim this home button'
                }}
                to={handleClaim}
              >
                {intl.formatMessage(MSG.claim)}
              </LinkButton>
              <LinkButton
                as={ReactRouterLink}
                color={theme.isLightThemeType ? 'brand.500' : 'brand.700'}
                border='1px solid'
                borderColor={theme.isLightThemeType ? 'blackAlpha.300 !important' : 'whiteAlpha.300 !important'}
                backgroundColor='whiteAlpha.100'
                _hover={{
                  color: theme.isLightThemeType ? 'brand.800' : 'brand.800'
                }}
                tracking={{
                  guid: 'oKxJw5LxTWnMXnAkuGiQEP',
                  ui_context: 'Own a Home',
                  descriptor: 'Enter a different address button'
                }}
                to={handleEnterAddress}
              >
                {intl.formatMessage(MSG.enterAddress)}
              </LinkButton>
            </>
          )}
          {!address && (
            <LinkButton
              as={ReactRouterLink}
              rightIcon={<Icon name='circle-arrow-right-bts' />}
              size='md'
              backgroundColor={theme.isLightThemeType ? 'brand.500' : 'brand.400'}
              color='white'
              _hover={{
                backgroundColor: theme.isLightThemeType ? 'brand.600' : 'brand.500',
                color: 'white'
              }}
              w='100%'
              to={buildRouteToHome}
              tracking={{
                guid: 'hokd1bexU8xZ6YXJGQjbWL',
                ui_context: 'Own a Home',
                descriptor: "Let's go button"
              }}
            >
              {intl.formatMessage(MSG.letsGo)}
            </LinkButton>
          )}
        </Flex>
      )}
    </Flex>
  )
}
